import { Camera, CameraOff } from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/design/components/button';
import type { MediaDevicesLike } from '@/features/persons/images/camera';
import { platformMedia } from '@/features/persons/images/camera';
import type { DeviceStorage } from '@/features/persons/images/devices';
import { ScanCamera } from '@/features/scan/camera';
import type { LoadedDetector } from '@/features/scan/detector';
import { ScanUpload } from '@/features/scan/upload';

/**
 * The gate's own ways of READING a band, offered where a band is BOUND.
 *
 * **Two screens bind a wristband to a record** — the registration form and the amendment screen's
 * Passes section — and until this existed both offered exactly one way in: type twenty-one
 * characters, or put the cursor in the field first and scan. The gate has had four ways since AD-32,
 * and two of them were already self-contained components with no gate coupling at all; they had
 * simply never been mounted anywhere else. This is that mounting, and nothing more: the decode lives
 * in `camera.tsx` and `upload.tsx` exactly as it does at the door, so the two places cannot come to
 * disagree about what a readable band is.
 *
 * **What is NOT here is the document-wide keystroke capture, and that is a decision.**
 * `useScanCapture` withholds a run of code-alphabet characters — which is every ASCII letter and
 * digit — until the operator pauses, and puts them back afterwards. On the gate that is invisible,
 * because the screen has one text field and it opts out. On a thirty-five field intake form it would
 * mean every name, passport number and telephone appearing a word at a time, a word behind, and keys
 * typed into a `<select>` vanishing entirely, since the replay drops what it cannot write back. The
 * reader still works on these screens the way it works in any browser: it types into the focused
 * field, which is what `data-scan-capture="off"` on that field declares.
 *
 * **Every button here declares `type="button"`.** The design-system `Button` sets no default, and the
 * registration form wraps this in a `<form>` — so an untyped control would submit the registration
 * when an operator pressed it to look at a wristband.
 *
 * **A decode closes the camera**, for the reason `camera.tsx` states in its own words: a band held in
 * front of a lens decodes thirty times a second, and the caller is what decides what happens next.
 */
export function BandReader({
  onCode,
  media = platformMedia(),
  storage = globalThis.localStorage,
  loadDetector,
}: Readonly<{
  /** A complete, canonical code. Both decoders run `parsePassCode` before they call this. */
  onCode: (code: string) => void;
  /** `null` when this browser exposes no camera API at all. */
  media?: MediaDevicesLike | null;
  /** Where the terminal remembers which camera it uses. The photograph panel's store, deliberately. */
  storage?: DeviceStorage;
  /** Injected, because `jsdom` runs no WebAssembly and decodes nothing. */
  loadDetector?: () => Promise<LoadedDetector>;
}>): ReactNode {
  const { t } = useTranslation();
  const [cameraOpen, setCameraOpen] = useState(false);

  // Spread rather than passed as `undefined`: the prop is optional and `exactOptionalPropertyTypes`
  // distinguishes "absent" from "present and undefined".
  const detectorProp = loadDetector === undefined ? {} : { loadDetector };

  const take = useCallback(
    (code: string) => {
      setCameraOpen(false);
      onCode(code);
    },
    [onCode],
  );

  return (
    <div className="grid gap-3" data-testid="band-reader">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="justify-self-start"
        data-testid="band-camera-toggle"
        onClick={() => {
          setCameraOpen((open) => !open);
        }}
      >
        {cameraOpen ? <CameraOff aria-hidden /> : <Camera aria-hidden />}
        {/*
          Its OWN catalogue keys, not the gate's. `scan.camera.open` is the same sentence as
          `person.images.startCamera` in both languages, and the photograph webcam is on both of
          these screens — so reusing it would put two identically named buttons, doing different
          things, in front of the same operator.
        */}
        {t(cameraOpen ? 'person.form.bandCameraClose' : 'person.form.bandCamera')}
      </Button>

      {cameraOpen && <ScanCamera media={media} storage={storage} {...detectorProp} onCode={take} />}

      <ScanUpload {...detectorProp} onCode={take} />
    </div>
  );
}
